import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { fetchPages } from "../lib/cms";
import { clinicInfo } from "../data";

export default function DynamicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPraktisk = slug === "praktisk";

  useEffect(() => {
    async function loadPage() {
      try {
        const pages = await fetchPages();
        // Normalize slug lookup
        const foundPage = pages.find(
          (p) => p.slug?.da === slug || p.slug === slug
        );
        setPage(foundPage);
      } catch (error) {
        console.error("Error loading dynamic page:", error);
      } finally {
        setLoading(false);
      }
    }
    loadPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  // If page not found, redirect to 404
  if (!page) {
    return <Navigate to="/404" replace />;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
      {/* Breadcrumb */}
      <nav className="flex mb-8 text-sm text-brand-600 font-medium">
        <Link to="/" className="hover:text-brand-800">Forside</Link>
        <span className="mx-2">/</span>
        <span className="text-brand-900 font-semibold">{page.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        {/* Main Content (8 cols) */}
        <article className="lg:col-span-8 space-y-8 text-left">
          {/* Page Image - Hidden on Praktisk page */}
          {page.images && page.images.length > 0 && !isPraktisk && (
            <div className="overflow-hidden rounded-3xl border border-brand-100 shadow-md">
              <img
                src={`${page.images[0]}?width=1000&quality=85&format=auto`}
                alt={page.title}
                className="w-full h-[400px] object-cover object-center"
              />
            </div>
          )}

          {/* Verbatim Content */}
          <div
            className="prose prose-brand max-w-none prose-headings:font-display prose-headings:font-bold prose-h1:text-3xl prose-h1:sm:text-4xl prose-h2:text-2xl prose-h3:text-xl prose-p:text-brand-800 prose-p:leading-relaxed prose-li:text-brand-800 prose-strong:text-brand-950 prose-strong:font-semibold"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />

          {/* Sygesikring "danmark" special full-width showcase for Praktisk page */}
          {isPraktisk && page.images && page.images.length > 1 && (
            <div className="mt-10 overflow-hidden rounded-3xl border border-brand-100 bg-brand-50/40 p-6 sm:p-8 shadow-sm flex flex-col md:flex-row gap-8 items-center">
              <div className="w-full md:w-1/2 space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100">
                  <i className="fa-solid fa-circle-check" />
                  <span>Få tilskud til din behandling</span>
                </div>
                <h3 className="text-xl font-bold font-display text-brand-950">
                  Sygesikring "danmark"
                </h3>
                <p className="text-sm text-brand-800 leading-relaxed">
                  Klinikken indberetter automatisk dine behandlinger direkte til Sygesikring "danmark". Du skal blot oplyse os om dit medlemskab, så sørger vi for resten, så du nemt og hurtigt får dit tilskud udbetalt.
                </p>
              </div>
              <div className="w-full md:w-1/2 overflow-hidden rounded-2xl border border-brand-100/80 bg-white p-4 shadow-xs flex items-center justify-center min-h-[160px]">
                <img
                  src={`${page.images[1]}?width=600&quality=90&format=auto`}
                  alt="Sygesikring danmark tilskud"
                  className="max-w-full max-h-[140px] object-contain"
                />
              </div>
            </div>
          )}

          {/* Additional Images (Standard Gallery for other pages) */}
          {page.images && page.images.length > 1 && !isPraktisk && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-8 border-t border-brand-100">
              {page.images.slice(1).map((img, idx) => (
                <div key={idx} className="overflow-hidden rounded-2xl border border-brand-100 shadow-xs aspect-4/3">
                  <img
                    src={`${img}?width=500&quality=80&format=auto`}
                    alt={`${page.title} detail ${idx + 1}`}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              ))}
            </div>
          )}
        </article>

        {/* Sidebar (4 cols) */}
        <aside className="lg:col-span-4 space-y-8 text-left sticky top-24">
          {/* Booking Card */}
          <div className="rounded-3xl bg-brand-900 text-white p-8 shadow-lg space-y-6">
            <h3 className="text-xl font-bold font-display text-white">Bestil tid i dag</h3>
            <p className="text-sm text-brand-200 leading-relaxed">
              Døjer du med smerter eller nedsat bevægelighed? Bestil en tid online eller ring til os for en uforpligtende snak.
            </p>
            <div className="space-y-4 pt-2">
              <a
                href={clinicInfo.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center rounded-full bg-white py-3.5 text-sm font-bold text-brand-900 shadow-sm hover:bg-brand-50 transition-all duration-200 hover:scale-102 active:scale-98 cursor-pointer"
              >
                Bestil tid online
              </a>
              <a
                href={`tel:${clinicInfo.phone}`}
                className="flex w-full items-center justify-center rounded-full bg-brand-800 border border-brand-700 py-3.5 text-sm font-bold text-white hover:bg-brand-700 transition-colors cursor-pointer"
              >
                <i className="fa-solid fa-phone mr-2" />
                <span className="whitespace-nowrap">{clinicInfo.phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4")}</span>
              </a>
            </div>
          </div>

          {/* Opening Hours Card */}
          <div className="rounded-3xl bg-white border border-brand-100 p-8 shadow-xs space-y-6">
            <h3 className="text-lg font-bold font-display text-brand-950 border-b border-brand-100 pb-3">
              Åbningstider
            </h3>
            <div className="space-y-3 text-sm text-brand-800">
              {clinicInfo.openingHours.map((item) => (
                <div key={item.day} className="flex justify-between border-b border-brand-50 pb-1.5">
                  <span>{item.day}</span>
                  <span className="font-semibold text-brand-900">{item.hours}</span>
                </div>
              ))}
              <div className="pt-2 text-xs text-brand-600">
                <p className="font-semibold text-brand-800">Telefontider:</p>
                <p className="mt-1">{clinicInfo.telefontider}</p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
